import React from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { AuditEventEnvelope } from '@/types';
import { UNAUTHENTICATED_EVENT, actorDisplayName, categoryMeta, describeEvent, eventTypeLabel, isPossibleProbe } from './auditMeta';
import { formatAuditValue } from '@/utils/formatters';
import ActorChip from './ActorChip';

interface AuditDetailDrawerProps {
  event: AuditEventEnvelope | null;
  onClose: () => void;
  /** Deposit events: filter the console to this deposit's full history. */
  onViewDepositHistory?: (event: AuditEventEnvelope) => void;
}

interface Change {
  field: string;
  old: unknown;
  new: unknown;
}

function extractChanges(details: Record<string, unknown> | null): Change[] {
  if (!details) return [];
  const raw = (details as any).changes;
  if (Array.isArray(raw)) return raw as Change[];
  return [];
}

const AuditDetailDrawer: React.FC<AuditDetailDrawerProps> = ({ event, onClose, onViewDepositHistory }) => {
  const navigate = useNavigate();
  if (!event) return null;

  const cat = categoryMeta(event.category);
  const sentence = describeEvent(event);
  const changes = extractChanges(event.details);
  const ts = new Date(event.ts);

  const copyJson = () => {
    try {
      navigator.clipboard?.writeText(JSON.stringify(event, null, 2));
    } catch {
      /* clipboard unavailable */
    }
  };

  const openDeposit = () => {
    if (event.entityType === 'deposit' && event.entityId) {
      navigate(`/deposits?deposit=${event.entityId}`);
      onClose();
    }
  };

  return (
    <div className="absolute inset-y-0 right-0 z-30 flex w-96 max-w-full flex-col border-l bg-white shadow-xl">
      <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-6 w-6 items-center justify-center rounded ${cat.bg}`}>
            <i className={`fas ${cat.icon} ${cat.color} text-xs`} />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-gray-900" title={event.eventType}>
              {eventTypeLabel(event.eventType)}
            </h3>
            <p className="text-[10px] text-gray-500">
              {cat.label} · <span className="font-mono">{event.eventType}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          title="Close details"
          aria-label="Close details"
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 text-xs">
        {/* Sentence summary */}
        <p className="mb-3 leading-snug text-gray-700">
          <span className="font-semibold text-gray-900">
            {event.actorType === 'system' ? 'System' : event.actorName || event.actorEmail || actorDisplayName(event)}
          </span>{' '}
          {sentence.verb}
          {sentence.change && <span className="font-medium text-gray-900"> {sentence.change}</span>}
          {sentence.onLabel && <span className="text-gray-500"> on {sentence.onLabel}</span>}
        </p>

        {event.eventType === UNAUTHENTICATED_EVENT && (
          <p
            className={`mb-3 rounded border px-2 py-1.5 text-[11px] leading-snug ${
              isPossibleProbe(event) ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50 text-gray-600'
            }`}
          >
            {isPossibleProbe(event)
              ? 'A request without a valid sign-in was refused. It came from a script-like client or asked for a path this app never uses, so it may be someone probing the site. Nothing was returned.'
              : 'A request without a valid sign-in was refused and nothing was returned. This is almost always a page left open after its sign-in expired, still asking the server for data.'}
          </p>
        )}

        {/* Envelope facts */}
        <dl className="mb-4 space-y-1">
          <Row label="When" value={format(ts, 'MMM d, yyyy • h:mm:ss a')} title={ts.toISOString()} />
          <Row label="Actor">
            <ActorChip event={event} />
          </Row>
          {event.clientName && <Row label="Client" value={event.clientName} />}
          {event.entityLabel && <Row label="Entity" value={event.entityLabel} />}
          {event.ipAddress && <Row label="IP" value={event.ipAddress} mono />}
        </dl>

        {/* Before/after diff */}
        {changes.length > 0 && (
          <div className="mb-4">
            <div className="mb-1 text-[10px] font-medium uppercase text-gray-500">Changes</div>
            <table className="w-full font-mono text-[11px]">
              <tbody>
                {changes.map((c, i) => (
                  <tr key={i} className="align-top">
                    <td className="py-0.5 pr-2 text-gray-500">{c.field}</td>
                    <td className="py-0.5 pr-1">
                      <span className="rounded bg-red-50 px-1 text-red-700">{formatAuditValue(c.old)}</span>
                    </td>
                    <td className="py-0.5 pr-1 text-gray-400">→</td>
                    <td className="py-0.5">
                      <span className="rounded bg-green-50 px-1 text-green-700">{formatAuditValue(c.new)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Raw details */}
        {event.details && (
          <div className="mb-4">
            <div className="mb-1 text-[10px] font-medium uppercase text-gray-500">Details</div>
            <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-[10px] text-gray-600">
              {JSON.stringify(event.details, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 border-t bg-gray-50 px-4 py-2">
        {event.entityType === 'deposit' && event.entityId && onViewDepositHistory && (
          <button
            type="button"
            onClick={() => onViewDepositHistory(event)}
            className="flex items-center gap-1 rounded border border-blue-300 bg-white px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
            title="Show every audit event for this deposit, all time"
          >
            <i className="fas fa-clock-rotate-left text-[10px]" aria-hidden="true" />
            View this deposit's full history
          </button>
        )}
        {event.entityType === 'deposit' && event.entityId && (
          <button
            type="button"
            onClick={openDeposit}
            className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-100"
            title="Open this deposit on the Deposits page"
          >
            <i className="fas fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
            Open deposit
          </button>
        )}
        <button
          type="button"
          onClick={copyJson}
          className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-100"
          title="Copy this event as JSON"
        >
          <i className="fas fa-copy text-[10px]" aria-hidden="true" />
          Copy JSON
        </button>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value?: string; title?: string; mono?: boolean; children?: React.ReactNode }> = ({
  label,
  value,
  title,
  mono,
  children,
}) => (
  <div className="flex items-start justify-between gap-3">
    <dt className="text-[10px] uppercase text-gray-400">{label}</dt>
    <dd className={`min-w-0 flex-1 text-right text-gray-700 ${mono ? 'font-mono text-[11px]' : ''}`} title={title}>
      {children ?? value}
    </dd>
  </div>
);

export default AuditDetailDrawer;
